import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from 'react-query';
import { citiesAPI } from '../services/api';
import { MapPin, Search, ChevronDown, X } from 'lucide-react';

const CityDropdown = ({ 
  value, 
  onChange, 
  placeholder = "Select city...", 
  disabled = false,
  error = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCities, setFilteredCities] = useState([]);
  const dropdownRef = useRef(null);

  // Get all cities
  const { data: citiesData, isLoading } = useQuery('cities', () => citiesAPI.getAll(), {
    refetchInterval: 300000, // Refetch every 5 minutes
  });

  // Filter cities based on search term
  useEffect(() => {
    if (citiesData?.cities) {
      const filtered = citiesData.cities.filter(city => {
        const cityName = city.name.toLowerCase();
        const routeName = city.route_name ? city.route_name.toLowerCase() : '';
        const searchLower = searchTerm.toLowerCase();
        
        return cityName.includes(searchLower) || routeName.includes(searchLower);
      });
      setFilteredCities(filtered);
    }
  }, [citiesData, searchTerm]);

  // Get selected city
  const selectedCity = citiesData?.cities?.find(city => city.id === parseInt(value));

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCitySelect = (city) => {
    onChange(city.id);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className={`w-full px-4 py-3 border rounded-lg font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors cursor-pointer ${
          disabled 
            ? 'bg-gray-50 border-gray-200 text-gray-500 cursor-not-allowed' 
            : error
            ? 'bg-red-50 border-red-200 text-red-900 hover:bg-red-100'
            : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50'
        }`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {selectedCity ? (
              <>
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                  <MapPin className="h-3 w-3 text-blue-600" />
                </div>
                <div className="text-left">
                  <p className="font-medium">{selectedCity.name}</p>
                  {selectedCity.route_name && (
                    <p className="text-xs text-gray-600">{selectedCity.route_name}</p>
                  )}
                </div>
              </>
            ) : (
              <span className="text-gray-500">
                {isLoading ? 'Loading cities...' : placeholder}
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {selectedCity && !disabled && (
              <button
                onClick={handleClear}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </div>
        </div>
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
          {/* Search Input */}
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search cities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                autoFocus
              />
            </div>
          </div>

          {/* Cities List */}
          <div className="max-h-48 overflow-y-auto">
            {isLoading ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                Loading cities...
              </div>
            ) : filteredCities.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                {searchTerm ? 'No cities found' : 'No cities available'}
              </div>
            ) : (
              filteredCities.map(city => (
                <div
                  key={city.id}
                  className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                  onClick={() => handleCitySelect(city)}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                      <MapPin className="h-3 w-3 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{city.name}</p>
                      {city.route_name && (
                        <p className="text-sm text-gray-600">{city.route_name}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CityDropdown; 